import React, { useEffect, useMemo, useState } from "react";
import { useVerifyEmail } from "../../hooks/auth";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  Fade,
  Button,
  Alert,
  CircularProgress,
  useTheme} from "@mui/material";
import { CheckCircleOutline, ErrorOutline } from "@mui/icons-material";
import { ApiError } from "../../error/errorTypes";
import { LOGIN_ROUTE_PATH } from "../../utils/constants/AppConstants";

const VerifyEmailConfirmPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
  const theme = useTheme();
  const navigate = useNavigate();
  const [success, setSuccess] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [redirectCountdown, setRedirectCountdown] = useState<number>(3);

  const { mutate: verifyEmail, isPending: isLoading } = useVerifyEmail();

  useEffect(() => {
   
    if (!token) {
      setError("Missing verification token.");
      setSuccess(false);
      return;
    }
    
    verifyEmail(token, {
      onSuccess: () => {
        setSuccess(true);
      },
      onError: (err) => {
        if (err instanceof ApiError) {
          setError(err.userMessage || err.message);
        } else {
          setError("Email verification failed. The link may be invalid or expired.");
        }
        setSuccess(false);
      }
    });
  }, [token, verifyEmail]);

  useEffect(() => {
    if (!success) return;
    const timer = setInterval(() => {
      setRedirectCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate(LOGIN_ROUTE_PATH);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [success, navigate]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${theme.palette.primary.main}15 0%, ${theme.palette.secondary.main}15 100%)`,
        display: "flex",
        alignItems: "center",
        py: 4}}
    >
      <Container maxWidth="sm">
        <Fade in={true} timeout={700}>
          <Card
            sx={{
              background: theme.palette.background.paper,
              backdropFilter: "blur(10px)",
              border: `1px solid ${theme.palette.divider}`}}
          >
            <CardContent sx={{ p: 5, textAlign: "center" }}>
              {isLoading && (
                <>
                  <CircularProgress sx={{ mb: 3 }} />
                  <Typography variant="h6" role="status" aria-live="polite">Verifying your email...</Typography>
                </>
              )}

              {!isLoading && success && (
                <>
                  <CheckCircleOutline sx={{ color: theme.palette.success.main, fontSize: 48, mb: 2 }} />
                  <Typography variant="h5" gutterBottom>You're all set!</Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
                    Your email has been verified. You can now sign in.
                  </Typography>
                  <Button variant="contained" onClick={() => navigate(LOGIN_ROUTE_PATH)}>
                    Go to sign in {redirectCountdown > 0 ? `(${redirectCountdown}s)` : ""}
                  </Button>
                </>
              )}

              {!isLoading && success === false && (
                <>
                  <ErrorOutline sx={{ color: theme.palette.error.main, fontSize: 48, mb: 2 }} />
                  <Typography variant="h5" gutterBottom>Email verification failed</Typography>
                  {error && (
                    <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>
                  )}
                  <Button variant="contained" onClick={() => navigate(LOGIN_ROUTE_PATH)}>Back to sign in</Button>
                </>
              )}
            </CardContent>
          </Card>
        </Fade>
      </Container>
    </Box>
  );
};

export default VerifyEmailConfirmPage;


